"use client";

import { useRef, type RefObject } from "react";
import { ProductCard } from "@farm-junction/design-library";
import LeadButton from "@/component/ui/Lead/LeadButton";
import MobileStickyCta, { MOBILE_STICKY_CTA_CLASS } from "@/component/pdp/MobileStickyCta";
import PdpModelSummaryCard from "@/component/pdp/revamp-components/PdpModelSummaryCard";
import ImagePageCarousel, { ImagePageThumbCarousel, useImageCarousel } from "./ImagePageCarousel";
import { useImagePageMedia } from "./ImagePageMedia";
import type { ImageGalleryItem, ImagePageGalleryData } from "./types";

type ImagePageHeroProps = {
    data: ImagePageGalleryData;
    brandName?: string;
    modelName?: string;
    reviewRating?: number;
    carId?: string;
    slug?: string;
    priceHref?: string;
    isMobile?: boolean;
    onImageClick?: (image: ImageGalleryItem) => void;
    useSplashPopup?: boolean;
};

const PRODUCT_CARD_CLASS = [
    "hidden w-full max-w-none overflow-hidden rounded-2xl font-sans lg:flex",
    "lg:sticky lg:top-28 lg:w-[340px] lg:max-w-[340px] lg:shrink-0",
    "xl:w-[360px] xl:max-w-[360px]",
    "[&_a.mt-3]:flex [&_a.mt-3]:w-full [&_a.mt-3]:justify-center",
    "[&_a.mt-3]:!rounded-[12px] [&_a.mt-3]:border [&_a.mt-3]:border-[#008C8B]",
    "[&_a.mt-3]:py-2.5 [&_a.mt-3]:text-[#008C8B]",
    "[&_img]:object-contain [&_img]:bg-[#F4F4F4]",
].join(" ");

export default function ImagePageHero({
    data,
    brandName = "",
    modelName = "",
    reviewRating = 0,
    carId,
    slug = "/",
    priceHref,
    isMobile = false,
    onImageClick,
    useSplashPopup = false,
}: ImagePageHeroProps) {
    const onRoadPriceHref = priceHref || slug;
    const offersLeadRef = useRef<HTMLDivElement>(null);
    const mobileCardRef = useRef<HTMLDivElement>(null);
    const { openLightbox } = useImagePageMedia();
    const images = data.images;
    const { startIndex, activeIndex, jumpKey, jumpTo } = useImageCarousel(images.length);
    const current = images[activeIndex] || images[0];
    const optimizeSummaryImage = (width: number) => {
        try {
            const url = new URL(data.summaryImage);
            url.searchParams.set("width", String(width));
            url.searchParams.set("format", "webp");
            url.searchParams.set("quality", "70");
            url.searchParams.delete("height");
            return url.toString();
        } catch {
            return data.summaryImage;
        }
    };

    const cardImageSrc = optimizeSummaryImage(340);
    const mobileCardImageSrc = optimizeSummaryImage(176);
    const summaryBrandName = brandName || data.carName.split(" ")[0] || "";
    const summaryModelName = modelName || data.carName.slice(summaryBrandName.length).trim() || data.carName;

    const triggerLead = (ref: RefObject<HTMLDivElement | null>) => {
        ref.current?.querySelector("button")?.click();
    };

    return (
        <section className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
            <div className="w-full min-w-0 lg:flex-1">
                <div className="-mx-5 sm:-mx-6 md:mx-0">
                    <ImagePageCarousel
                        variant="hero"
                        slides={images}
                        startIndex={startIndex}
                        activeIndex={activeIndex}
                        jumpKey={jumpKey}
                        frameClassName="bg-[#F4F4F4]"
                        priority
                        isMobile={isMobile}
                        onIndexChange={(index) => {
                            if (index === activeIndex) return;
                            jumpTo(index);
                        }}
                        onNavigate={jumpTo}
                        onSlideClick={(index) => {
                            const image = images[index];
                            if (!image) return;
                            if (onImageClick) onImageClick(image);
                            else openLightbox(image.id, "all", "all");
                        }}
                    />
                </div>

                <div className="mt-3 -mx-5 sm:-mx-6 md:mx-0">
                    <ImagePageThumbCarousel
                        slides={images}
                        activeId={current?.id}
                        onThumbClick={jumpTo}
                        frameClassName="bg-[#F4F4F4]"
                        itemsPerView={7.2}
                        mobileItemsPerView={4.2}
                    />
                </div>
            </div>

            <PdpModelSummaryCard
                className="font-sans lg:hidden"
                cardRef={mobileCardRef}
                brandName={summaryBrandName}
                modelName={summaryModelName}
                href={slug}
                productImageSrc={mobileCardImageSrc}
                rating={reviewRating}
                leftLabel={data.location}
                leftValue={data.priceLabel}
                rightLabel="Total Images"
                rightValue={String(data.imageCount)}
                footer={
                    <>
                        <LeadButton
                            carId={carId}
                            carName={data.carName}
                            btnText={data.offersButtonText}
                            fullWidth
                            useSplashPopup={useSplashPopup}
                            className="w-full cursor-pointer rounded-lg bg-[#008c8b] py-2.5 text-[13px] font-semibold text-white hover:bg-[#007776]"
                        />
                    </>
                }
            />

            <ProductCard
                className={PRODUCT_CARD_CLASS}
                onPrimaryCtaClick={() => triggerLead(offersLeadRef)}
                price={data.priceLabel}
                priceSuffixText={data.location}
                primaryCtaLabel={data.offersButtonText}
                productImageSrc={cardImageSrc}
                secondaryCtaHref={onRoadPriceHref}
                secondaryCtaLabel={data.priceButtonText}
                slotProps={{
                    headingProps: { level: "p", variant: "p", title: data.carName },
                    imgProps: {
                        alt: `${data.carName} Image`,
                        fetchPriority: "low",
                        loading: "lazy",
                        height: 178,
                        width: 340,
                        className: "w-full object-contain bg-[#F4F4F4]",
                    },
                    priceProps: { className: "font-semibold" },
                    priceSuffixProps: { className: "text-[#737373]" },
                    primaryCtaProps: {
                        className: "!rounded-[12px] bg-[#008C8B] py-2.5 text-white hover:bg-[#008C8B]/90",
                        title: data.offersButtonText,
                    },
                    secondaryCtaProps: { title: data.priceButtonText },
                    specsItemProps: { className: "bg-transparent p-0" },
                }}
                slug={slug}
                specs={[
                    { label: "Images", value: String(data.imageCount) },
                    ...(data.colorCount > 0
                        ? [{ label: "Colors", value: String(data.colorCount) }]
                        : []),
                ]}
                title={data.carName}
            />

            <div ref={offersLeadRef} className="hidden">
                <LeadButton carId={carId} carName={data.carName} btnText={data.offersButtonText} className="rounded" useSplashPopup={useSplashPopup} />
            </div>

            <MobileStickyCta anchorRef={mobileCardRef}>
                <LeadButton carId={carId} carName={data.carName} btnText={data.offersButtonText} className={MOBILE_STICKY_CTA_CLASS} useSplashPopup={useSplashPopup} />
            </MobileStickyCta>
        </section>
    );
}
